import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { CashContractUpgradeContext } from '@pokerroom/types';
import { api } from '../../lib/api';
import { useCashIntent, useCashScope } from '../accounting/cash/useCashIntent';
import type { CashIntent } from '../accounting/cash/cashRecovery';
import { CashRecoveryNotice } from '../accounting/cash/CashRecoveryNotice';
export function CashContractUpgradePanel() {
  const { t } = useTranslation('accounting');
  const scope = useCashScope(),
    qc = useQueryClient();
  const cash = useCashIntent(scope, 'contract-upgrade');
  const [amount, setAmount] = useState(''),
    [confirmed, setConfirmed] = useState(false);
  const context = useQuery({
    queryKey: ['cash-contract-upgrade', scope],
    retry: false,
    queryFn: () => api.get<CashContractUpgradeContext>('/accounting/cash-contract-upgrade'),
  });
  useEffect(() => {
    setConfirmed(false);
    setAmount('');
  }, [scope, context.data?.version]);
  async function submit(retained?: CashIntent) {
    if (
      !retained &&
      (!context.data?.canUpgrade ||
        context.isError ||
        context.isFetching ||
        !confirmed ||
        !/^0(?:\.0{1,2})?$/.test(amount) ||
        cash.intent)
    )
      return;
    const key = crypto.randomUUID(),
      now = Date.now();
    const record: CashIntent = retained ?? {
      recordVersion: 1,
      scope,
      action: 'UPGRADE_CASH_CONTRACT',
      stage: 'UNKNOWN',
      operationKey: key,
      createdAt: now,
      lastSeenAt: now,
      body: {
        operationKey: key,
        expectedVersion: context.data!.version,
        actualRevenueAmount: amount,
        confirmed,
      },
    };
    const result = await cash.post<{ operationKey: string; cashBagContractVersion: number }>(
      record,
      '/accounting/cash-contract-upgrade',
      (v) => v?.operationKey === record.operationKey && v.cashBagContractVersion === 2,
    );
    if (result) {
      cash.committed(record);
      await qc.invalidateQueries({ queryKey: ['counted-opening-context'] });
      await context.refetch();
    }
  }
  return (
    <section className="border rounded p-3 space-y-3" aria-label={t('upgrade.title')}>
      <h4 className="font-semibold">{t('upgrade.title')}</h4>
      <p>{t('upgrade.help')}</p>
      {context.isError && <p role="alert">{t('counted.configDenied')}</p>}
      {context.data && (
        <>
          <p>
            {t('upgrade.safe', { amount: context.data.safeAmount ?? t('counted.unavailable') })}
          </p>
          <p>
            {context.data.cashBagContractVersion === 1 &&
              context.data.blockers
                .map((code) =>
                  t(`upgrade.blockers.${code}`, { defaultValue: t('upgrade.blocked') }),
                )
                .join(', ')}
          </p>
          {context.data.cashBagContractVersion === 2 ? (
            <p role="status">{t('upgrade.done')}</p>
          ) : (
            <>
              <label>
                {t('upgrade.count')}
                <input
                  className="input input-bordered w-full"
                  inputMode="decimal"
                  value={amount}
                  disabled={cash.busy || !!cash.intent}
                  onChange={(e) => {
                    setAmount(e.target.value);
                    setConfirmed(false);
                  }}
                />
              </label>
              <label className="flex min-h-11 gap-2">
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                />
                {t('upgrade.confirm')}
              </label>
              <button
                className="btn min-h-11"
                disabled={
                  cash.busy ||
                  !!cash.intent ||
                  context.isFetching ||
                  context.isError ||
                  !context.data.canUpgrade ||
                  !confirmed ||
                  !/^0(?:\.0{1,2})?$/.test(amount)
                }
                onClick={() => void submit()}
              >
                {t('upgrade.title')}
              </button>
            </>
          )}
        </>
      )}
      {cash.intent?.body && cash.intent.stage !== 'COMMITTED' && (
        <button className="btn" disabled={cash.busy} onClick={() => void submit(cash.intent!)}>
          {t('cashWorkflow.retry')}
        </button>
      )}
      {cash.intent?.stage === 'REJECTED' && (
        <button className="btn" onClick={() => cash.startNew()}>
          {t('cashWorkflow.next')}
        </button>
      )}
      <CashRecoveryNotice error={cash.error} />
    </section>
  );
}
